import React, { useMemo } from 'react';
import { IHelpArticle } from './help-articles';
import { getTheme, IStyle, ITheme, mergeStyleSets } from '@fluentui/react';
import { ComponentModel, getComponentId, MarkdownComponent } from '../../parser/core';
import { toMarkdownComponentModels } from '../../parser';
import { ITimingSpecComponentModel } from '../../parser/timing-spec';
import { forEach } from '../../../../framework/utils/collection';
import { PRIVATE_MARKDOWN_HELP_VIEWER_COMPONENT } from './help-component';

interface IPRIVATE_MARKDOWN_HELP_VIEWER_VIEWPORTProps {
  article: IHelpArticle;
}

const getStyleClasses = (theme: ITheme) => {
  const root: IStyle = {
    backgroundColor: theme.palette.white,
    fontSize: theme.fonts.medium.fontSize,
    flexGrow: 1,
    padding: '1.5em',
    paddingBottom: '2em',
    borderTop: `1px solid ${theme.palette.neutralQuaternary}`,

    '& b': {
      textDecoration: 'underline',
      fontWeight: 'normal',
    }
  };

  return mergeStyleSets({
    root,
  });
}

const distillMarkdown = (markdown: string[]) => {
  const distilled: string[] = [];

  forEach(markdown, line => {
    if(line.match(/^\s*==[^=]/)) {
      const appendage = line.replace(/^\s*==/, '');
      const idx = distilled.length - 1;
      distilled[idx] = `${distilled[idx]} ${appendage}`;
    } else {
      distilled.push(line);
    }
  });

  return distilled;
}

const getComponentModel = (article: IHelpArticle) => {
  const content = distillMarkdown(article.content);
  const markdown = content.join('\n');
  const models = toMarkdownComponentModels(markdown);
  
  const distilledModels: ComponentModel[] = [];

  let currentTimingSection: ITimingSpecComponentModel | null = null;

  forEach(models, model => {
    // starting a new timed section
    if(model.type === MarkdownComponent.TimingSpec) {
      currentTimingSection = model as ITimingSpecComponentModel;
    } else if(currentTimingSection !== null) { // collecting timed section content
      // encountered end section marker
      if(model.type === MarkdownComponent.Paragraph && model.content[0].content.trim() === "#end$") {
        distilledModels.push(currentTimingSection);
        currentTimingSection = null;
      } else { // typical timed section content
        currentTimingSection.notes.push(model);
      }
    } else { // typical help content
      distilledModels.push(model);
    }
  });

  return distilledModels;
};

export const PRIVATE_MARKDOWN_HELP_VIEWER_VIEWPORT: React.FunctionComponent<IPRIVATE_MARKDOWN_HELP_VIEWER_VIEWPORTProps> = (props) => {
  const { article } = props;
  const theme = getTheme();
  const classes = getStyleClasses(theme);

  const componentModel = useMemo(() => {
    return getComponentModel(article);
  }, [article]);

  return (
    <section className={classes.root}>
      {componentModel.map(model => <PRIVATE_MARKDOWN_HELP_VIEWER_COMPONENT
        key={getComponentId(model)}
        model={model}
      />)}
    </section>
  );
};
